// src/hooks/useAppLanguage.ts
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

/**
 * @typedef AppLanguageHookResult
 * @property {string} currentLanguage - The current active language code (e.g., 'en', 'zh').
 * @property {(lng: string) => void} setLanguage - Function to change the language to a specific one.
 * @property {() => void} toggleLanguage - Function to toggle the language between 'en' and 'zh'.
 * @property {import('i18next').TFunction} t - The translation function from i18next.
 * @property {import('i18next').i18n} i18n - The i18n instance.
 */

/**
 * Custom hook for managing application language.
 * Provides the current language, functions to change or toggle language,
 * and the i18next t function.
 * @returns {AppLanguageHookResult}
 */
export const useAppLanguage = () => {
  const { t, i18n } = useTranslation();

  const currentLanguage = i18n.language;

  /**
   * Changes the application language to the specified language code.
   * @param {string} lng - The language code to switch to (e.g., 'en', 'zh').
   */
  const setLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng);
  }, [i18n]);

  /**
   * Toggles the application language between English ('en') and Chinese ('zh').
   */
  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
  }, [i18n]);

  return {
    currentLanguage,
    setLanguage,
    toggleLanguage,
    t,
    i18n, // Exposing i18n instance for flexibility
  };
};
